"use client";

import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  isAddress,
  maxUint256,
  parseEventLogs,
  parseUnits,
  zeroAddress,
} from "viem";
import {
  useAccount,
  usePublicClient,
  useWalletClient,
  useWriteContract,
} from "wagmi";
import { useAuth } from "@/context/auth";
import { BUY_NFT_ABI, buyNftPlacementArgs } from "@/contract/abi/buy-nft";
import { ERC20_ABI } from "@/contract/abi/erc20";
import { GATEWAY_ABI } from "@/contract/abi/gateway";
import ContractAddress from "@/contract/addresses";
import { usePriceConverter } from "@/hooks/price/usePriceConverter";
import { useContractErrorHandler } from "@/hooks/useContractErrorHandler";
import { $api } from "@/lib/api-client";
import {
  formatTokenAmount,
  InsufficientBalanceError,
} from "@/lib/checkout/insufficientBalanceError";
import { persistPendingFinalize } from "@/lib/checkout/pending-finalize";
import { createTransactionLogger } from "@/lib/transaction-logger";
import type { components } from "@/types/api";
import { assertLiveSigner } from "./assertLiveSigner";

type CartItemResponseDto = components["schemas"]["CartItemResponseDto"];
type PreparedItemDto = components["schemas"]["PreparedItemDto"];

type ContractPurchaseItem = {
  seller: `0x${string}`;
  sv: bigint;
  bv: bigint;
  paymentTokenId: bigint;
  buyerAmount: bigint;
  tokenUri: string;
};

type CheckoutPurchaseParams = {
  cartItems: CartItemResponseDto[];
  shippingAddressId: string;
  /** Binary-tree placement target, from usePlacementResolution. */
  parentAddress: string;
  /**
   * Who referred the buyer, from usePlacementResolution -- the zero address
   * for a buyer who is not (or will never be) referred. Distinct from
   * `parentAddress`: the contract only reads this on a buyer's first-ever
   * purchase and locks it forever after that.
   */
  referrerAddress: string;
  orderType: "product" | "service";
  /** Preparation ID from POST /order/prepare */
  preparationId: string;
  /** Prepared items with token_uris from POST /order/prepare */
  preparedItems: PreparedItemDto[];
};

type CheckoutPurchaseResult = {
  success: boolean;
  itemCount: number;
  txHash: `0x${string}`;
  paidPrice: components["schemas"]["PriceDto"];
  itemsPayment: components["schemas"]["ItemPaymentDto"][];
};

type UseCheckoutPurchaseOptions = {
  onSuccess?: (data: CheckoutPurchaseResult) => void;
  onError?: (error: Error) => void;
};

/**
 * Map settlement currency to paymentTokenId
 * 1 = USDT, 2 = UVM
 */
const getPaymentTokenId = (currency: "USDT" | "UVM"): bigint => {
  switch (currency) {
    case "USDT":
      return "1" as unknown as bigint;
    case "UVM":
      return "2" as unknown as bigint;
    default:
      return "1" as unknown as bigint;
  }
};

/**
 * Get token decimals for the settlement currency
 * USDT = 6 decimals, UVM = 18 decimals
 */
const getTokenDecimals = (currency: "USDT" | "UVM"): number => {
  switch (currency) {
    case "USDT":
      return 6;
    case "UVM":
      return 18;
    default:
      return 18;
  }
};

/**
 * Transform prepared items into PurchaseItem[] for the contract.
 * Prepared items are already expanded by quantity (1 per unit),
 * so we iterate over them directly and look up cart item data by product_id.
 */
export const transformToPurchaseItems = (
  cartItems: CartItemResponseDto[],
  preparedItems: PreparedItemDto[],
  priceConverter: ReturnType<typeof usePriceConverter>,
): ContractPurchaseItem[] => {
  if (!priceConverter.rates) {
    throw new Error("Exchange rates not loaded");
  }

  // Create a map of product_id to cart item for lookups
  const cartItemMap = new Map<string, CartItemResponseDto>();
  for (const item of cartItems) {
    cartItemMap.set(item.product_id, item);
  }

  return preparedItems.map((prepared) => {
    const cartItem = cartItemMap.get(prepared.product_id);
    if (!cartItem) {
      throw new Error(
        `Cart item not found for prepared product: ${prepared.product_id}`,
      );
    }

    const vendor = cartItem.product?.vendor;
    const sellerAddress = vendor?.user?.wallet_address || "";

    if (!sellerAddress) {
      throw new Error(
        `Vendor wallet address not found for product: ${cartItem.product?.title}`,
      );
    }

    if (!isAddress(sellerAddress)) {
      throw new Error(
        `Invalid seller wallet address for product: ${cartItem.product?.title}. Got: ${sellerAddress}`,
      );
    }

    const settlementCurrency = normalizeSettlementCurrency(
      cartItem.price.seller_settlement_currency,
    );

    // Use per-unit prices from prepared item
    const { svInSettlementCurrency } =
      priceConverter.convertToSettlementCurrency(
        {
          seller_share_unit: prepared.sv_value,
          seller_share_currency: cartItem.price.seller_share_currency,
          bv: prepared.bv_value,
        },
        settlementCurrency,
      );

    // Mixed-decimal packing (BUG-28): the seller share is in settlement-currency
    // decimals (6 for USDT, 18 for UVM), but BV is ALWAYS 18-dec UVM. sv packs
    // both; buyerAmount carries ONLY the settlement share for the line-70 balance
    // check; the contract recovers the share via plain subtraction (sv - bv).
    const settlementDecimals = getTokenDecimals(settlementCurrency);
    const svInSettlementWei = parseUnits(
      `${svInSettlementCurrency}`,
      settlementDecimals,
    );
    const bvInUvmWei = parseUnits(`${prepared.bv_value}`, 18);

    return {
      seller: sellerAddress as `0x${string}`,
      sv: bvInUvmWei + svInSettlementWei,
      bv: bvInUvmWei,
      paymentTokenId: getPaymentTokenId(settlementCurrency),
      buyerAmount: svInSettlementWei,
      tokenUri: prepared.token_uri,
    };
  });
};

/**
 * Normalize settlement currency string to "UVM" | "USDT"
 */
const normalizeSettlementCurrency = (
  currency: string | null | undefined,
): "UVM" | "USDT" => {
  if (!currency) return "UVM";
  const upper = currency.toUpperCase();
  if (upper === "USDT" || upper === "USD") return "USDT";
  return "UVM";
};

/**
 * Compute the exact token balances the contract requires, summed across the cart.
 * Used for the pre-flight BALANCE CHECK only — approvals are set to maxUint256.
 *
 * The contract has TWO gates per item (Marketplace721Minter.sol + Payments.sol):
 *
 *   Gate A — Market pre-check (Marketplace721Minter.sol, patched for BUG-28):
 *     require(balanceOf(paymentToken) >= item.buyerAmount)
 *     where buyerAmount = the seller share in settlement-currency wei (NOT share+bv).
 *
 *   Gate B — Payment pulls (Payments.sol:37-83):
 *     t1 (seller token = USDT when tokenId=1, UVM when tokenId=2) pulls: s_value = sv - bv
 *     t2 (always UVM) pulls: b_value + (b_value * 3) / 100  ≈ b_value * 1.03
 *
 * Decimals are MIXED: the seller share uses settlement decimals (6 USDT / 18 UVM),
 * but BV is ALWAYS 18-dec UVM. Per item, all math in bigint:
 *   paymentTokenId === 1 (USDT):
 *     requiredUSDT += share (6-dec)            // Gate A = buyerAmount = share only
 *     requiredUVM  += bv(18-dec) * 103n / 100n // Gate B, bv path, always UVM
 *   paymentTokenId === 2 (UVM):
 *     requiredUVM  += share(18-dec) + bv(18-dec) * 103n / 100n
 *
 * Keep in lockstep with transformToPurchaseItems (same mixed-decimal scheme).
 */
export const calculateRequiredBalances = (
  cartItems: CartItemResponseDto[],
  priceConverter: ReturnType<typeof usePriceConverter>,
): { requiredUVM: bigint; requiredUSDT: bigint } => {
  if (!priceConverter.rates) {
    throw new Error("Exchange rates not loaded");
  }

  let requiredUVM = BigInt(0);
  let requiredUSDT = BigInt(0);

  for (const item of cartItems) {
    const settlementCurrency = normalizeSettlementCurrency(
      item.price.seller_settlement_currency,
    );
    const decimals = getTokenDecimals(settlementCurrency);

    const { svInSettlementCurrency } =
      priceConverter.convertToSettlementCurrency(
        {
          seller_share_unit: item.price.seller_share_unit,
          seller_share_currency: item.price.seller_share_currency,
          bv: item.price.bv,
        },
        settlementCurrency,
      );

    // NOTE: item.price is the cart row's price, which CartService.addToCart
    // stores already summed across quantity (it adds the product price on every
    // increment). It is the line total, not a unit price — so quantity must NOT
    // be applied again here. Doing so overstated the requirement by a factor of
    // `quantity` and rejected buyers who could actually afford the order.
    const sv = parseUnits(`${svInSettlementCurrency}`, decimals);
    // BV is ALWAYS 18-dec UVM, regardless of settlement currency. Parsing it at
    // the settlement decimals (6 for USDT) understated requiredUVM by 1e12x.
    const bvUvm = parseUnits(`${Number(item.price.bv)}`, 18);

    if (settlementCurrency === "USDT") {
      // Gate A checks buyerAmount = seller share only (settlement decimals)
      requiredUSDT += sv;
      // Gate B, bv path: Payments pulls b_value + (b_value * 3) / 100 as UVM (18-dec)
      requiredUVM += (bvUvm * BigInt(103)) / BigInt(100);
    } else if (settlementCurrency === "UVM") {
      // Gate B: Payments pulls (sv) + (bv * 1.03) from UVM — both 18-dec
      requiredUVM += sv + (bvUvm * BigInt(103)) / BigInt(100);
    }
  }

  return { requiredUVM, requiredUSDT };
};

/**
 * Build paid_price and items_payment for order creation.
 * Iterates over preparedItems (already expanded by quantity, 1 per unit)
 * so each entry maps 1:1 with a contract PurchaseItem and a NewOrderEvent.
 */
const buildOrderPaymentData = (
  cartItems: CartItemResponseDto[],
  preparedItems: PreparedItemDto[],
  orderIds: bigint[],
): {
  paidPrice: components["schemas"]["PriceDto"];
  itemsPayment: components["schemas"]["ItemPaymentDto"][];
} => {
  // Create a map of product_id to cart item for lookups
  const cartItemMap = new Map<string, CartItemResponseDto>();
  for (const item of cartItems) {
    cartItemMap.set(item.product_id, item);
  }

  const primaryCurrency = cartItems[0]?.price.seller_share_currency || "UVM";
  const primarySettlementCurrency = normalizeSettlementCurrency(
    cartItems[0]?.price.seller_settlement_currency,
  );

  let totalBv = 0;
  let totalSv = 0;
  const itemsPayment: components["schemas"]["ItemPaymentDto"][] = [];

  for (let i = 0; i < preparedItems.length; i++) {
    const prepared = preparedItems[i];
    const cartItem = cartItemMap.get(prepared.product_id);

    totalBv += prepared.bv_value;
    totalSv += prepared.sv_value;

    itemsPayment.push({
      product_id: prepared.product_id,
      paid_price: {
        bv: prepared.bv_value.toString(),
        seller_share_unit: prepared.sv_value.toString(),
        seller_share_currency:
          cartItem?.price.seller_share_currency || primaryCurrency,
        seller_settlement_currency: normalizeSettlementCurrency(
          cartItem?.price.seller_settlement_currency,
        ),
      },
      order_id: orderIds[i] !== undefined ? orderIds[i].toString() : undefined,
      item_id: prepared.id,
    });
  }

  const paidPrice: components["schemas"]["PriceDto"] = {
    bv: totalBv.toString(),
    seller_share_unit: totalSv.toString(),
    seller_share_currency: primaryCurrency,
    seller_settlement_currency: primarySettlementCurrency,
  };

  return { paidPrice, itemsPayment };
};

export const useCheckoutPurchase = (options?: UseCheckoutPurchaseOptions) => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { handleContractError } = useContractErrorHandler();
  const priceConverter = usePriceConverter();
  const updateUserRole = $api.useMutation("patch", "/user/me/referrer");
  const t = useTranslations("checkout.purchase");
  const tc = useTranslations("checkout");
  const te = useTranslations("errors");

  const { writeContractAsync } = useWriteContract();

  const mutationFn = async (params: CheckoutPurchaseParams) => {
    const logger = createTransactionLogger("checkout", address ?? "unknown");

    try {
      logger.step("validation", {
        orderType: params.orderType,
        cartItemCount: params.cartItems?.length,
        preparedItemCount: params.preparedItems?.length,
        preparationId: params.preparationId,
      });

      if (!address || !publicClient) throw new Error(te("walletNotConnected"));

      // Identity pre-flight against the WALLET, not against another cache.
      // Comparing wagmi's cached address to the JWT proves nothing when both
      // are stale — they agree with each other while `msg.sender` is someone
      // else. See assertLiveSigner.
      await assertLiveSigner({
        walletClient,
        cachedAddress: address,
        sessionWallet: useAuth.getState().user?.wallet_address,
        mismatchMessage: tc("referrer.walletMismatch"),
      });

      const { cartItems, parentAddress, referrerAddress, preparedItems } =
        params;
      if (!cartItems || cartItems.length === 0) throw new Error(t("emptyCart"));
      if (!preparedItems || preparedItems.length === 0)
        throw new Error(t("notPrepared"));
      if (!priceConverter.rates) throw new Error(t("ratesLoading"));
      if (!isAddress(parentAddress))
        throw new Error(t("invalidReferral", { address: parentAddress }));
      if (!isAddress(referrerAddress))
        throw new Error(t("invalidReferral", { address: referrerAddress }));

      // Transform prepared items to contract format
      logger.step("transform_items", {
        cartPrices: cartItems.map((item) => ({
          product_id: item.product_id,
          bv: item.price.bv,
          sv: item.price.seller_share_unit,
          currency: item.price.seller_share_currency,
          settlement: item.price.seller_settlement_currency,
          quantity: item.quantity,
        })),
      });
      const purchaseItems = transformToPurchaseItems(
        cartItems,
        preparedItems,
        priceConverter,
      );

      // Balance-check amounts (contract-exact). Approvals below use maxUint256.
      const { requiredUVM, requiredUSDT } = calculateRequiredBalances(
        cartItems,
        priceConverter,
      );
      logger.step("amounts_calculated", {
        requiredUVM: requiredUVM.toString(),
        requiredUSDT: requiredUSDT.toString(),
        itemCount: purchaseItems.length,
      });

      const waitAndVerifyTransaction = async (
        txHash: `0x${string}`,
        stepName: string,
        options?: { allowZeroLogs?: boolean },
      ) => {
        logger.step(`${stepName}_confirm`, { txHash });
        const receipt = await publicClient.waitForTransactionReceipt({
          hash: txHash,
        });
        if (
          receipt.status === "reverted" ||
          (typeof receipt.status === "number" && receipt.status === 0)
        ) {
          throw new Error(`${stepName} transaction reverted`);
        }
        // Calling an address with no deployed code (a stale/misconfigured
        // contract address) is itself a trivial EVM success -- `status`
        // alone cannot tell a real purchase from a no-op that silently did
        // nothing. Every event-emitting path through here (ERC20
        // approve/transfer and NFT mint) is required to emit at least one
        // log. Log-free storage writes must opt in and verify their state
        // separately after the receipt.
        if (receipt.logs.length === 0 && !options?.allowZeroLogs) {
          throw new Error(
            `${stepName} transaction produced no on-chain effect (0 events) -- the contract address is likely misconfigured or has no code`,
          );
        }
        return receipt;
      };

      // Step 1: UVM allowance (set to maxUint256)
      logger.step("uvm_allowance_check");
      const currentUVMAllowance = await publicClient.readContract({
        address: ContractAddress.UVM,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [address, ContractAddress.GATEWAY],
      });

      if (currentUVMAllowance !== maxUint256) {
        logger.step("uvm_approval_request", {
          current: currentUVMAllowance.toString(),
          requesting: "maxUint256",
        });
        const uvmTxHash = await writeContractAsync({
          address: ContractAddress.UVM,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [ContractAddress.GATEWAY, maxUint256],
        });
        await waitAndVerifyTransaction(uvmTxHash, "uvm_approval");

        const newUVMAllowance = await publicClient.readContract({
          address: ContractAddress.UVM,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [address, ContractAddress.GATEWAY],
        });
        if (newUVMAllowance !== maxUint256) {
          throw new Error("UVM allowance approval failed or insufficient");
        }
      }

      // Step 2: USDT allowance (set to maxUint256)
      logger.step("usdt_allowance_check");
      const currentUSDTAllowance = await publicClient.readContract({
        address: ContractAddress.USDT,
        abi: ERC20_ABI,
        functionName: "allowance",
        args: [address, ContractAddress.GATEWAY],
      });

      if (currentUSDTAllowance !== maxUint256) {
        logger.step("usdt_approval_request", {
          current: currentUSDTAllowance.toString(),
          requesting: "maxUint256",
        });
        const usdtTxHash = await writeContractAsync({
          address: ContractAddress.USDT,
          abi: ERC20_ABI,
          functionName: "approve",
          args: [ContractAddress.GATEWAY, maxUint256],
        });
        await waitAndVerifyTransaction(usdtTxHash, "usdt_approval");

        const newUSDTAllowance = await publicClient.readContract({
          address: ContractAddress.USDT,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [address, ContractAddress.GATEWAY],
        });
        if (newUSDTAllowance !== maxUint256) {
          throw new Error("USDT allowance approval failed or insufficient");
        }
      }

      // Step 3: Final allowance verification
      logger.step("final_allowance_verify");
      const [finalUVMAllowance, finalUSDTAllowance] = await Promise.all([
        publicClient.readContract({
          address: ContractAddress.UVM,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [address, ContractAddress.GATEWAY],
        }),
        publicClient.readContract({
          address: ContractAddress.USDT,
          abi: ERC20_ABI,
          functionName: "allowance",
          args: [address, ContractAddress.GATEWAY],
        }),
      ]);

      if (finalUVMAllowance !== maxUint256) {
        throw new Error("Insufficient UVM allowance for purchase");
      }
      if (finalUSDTAllowance !== maxUint256) {
        throw new Error("Insufficient USDT allowance for purchase");
      }

      // Step 4: Gateway allowance (set to maxUint256)
      logger.step("gateway_allowance_check");
      const currentGatewayAllowance = await publicClient.readContract({
        address: ContractAddress.GATEWAY,
        abi: GATEWAY_ABI,
        functionName: "getAllowance",
        args: [address, ContractAddress.MARKET_NFT],
      });

      if (currentGatewayAllowance !== maxUint256) {
        logger.step("gateway_allowance_request", {
          current: currentGatewayAllowance?.toString(),
          requesting: "maxUint256",
        });
        const gatewayTxHash = await writeContractAsync({
          address: ContractAddress.GATEWAY,
          abi: GATEWAY_ABI,
          functionName: "setAllowance",
          args: [ContractAddress.MARKET_NFT, maxUint256],
        });
        await waitAndVerifyTransaction(gatewayTxHash, "gateway_allowance", {
          allowZeroLogs: true,
        });

        const newGatewayAllowance = await publicClient.readContract({
          address: ContractAddress.GATEWAY,
          abi: GATEWAY_ABI,
          functionName: "getAllowance",
          args: [address, ContractAddress.MARKET_NFT],
        });
        if (newGatewayAllowance !== maxUint256) {
          throw new Error("Gateway allowance setup failed or insufficient");
        }
      }

      // Step 4.5: Balance check
      logger.step("balance_check");
      const [uvmBalance, usdtBalance] = await Promise.all([
        publicClient.readContract({
          address: ContractAddress.UVM,
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [address],
        }),
        publicClient.readContract({
          address: ContractAddress.USDT,
          abi: ERC20_ABI,
          functionName: "balanceOf",
          args: [address],
        }),
      ]);
      logger.step("balance_result", {
        uvmBalance: uvmBalance.toString(),
        usdtBalance: usdtBalance.toString(),
        requiredUVM: requiredUVM.toString(),
        requiredUSDT: requiredUSDT.toString(),
      });

      if (requiredUVM > BigInt(0) && uvmBalance < requiredUVM) {
        throw new InsufficientBalanceError(
          te("insufficientUVM", {
            needed: formatTokenAmount(requiredUVM - uvmBalance, 18),
          }),
        );
      }
      if (requiredUSDT > BigInt(0) && usdtBalance < requiredUSDT) {
        throw new InsufficientBalanceError(
          te("insufficientUSDT", {
            needed: formatTokenAmount(requiredUSDT - usdtBalance, 6),
          }),
        );
      }

      // Step 5: buyNft — log the exact contract inputs
      logger.step("buy_nft", {
        contractAddress: ContractAddress.MARKET_NFT,
        parentAddress,
        referrerAddress,
        itemCount: purchaseItems.length,
        items: purchaseItems.map((item) => ({
          seller: item.seller,
          sv: item.sv.toString(),
          bv: item.bv.toString(),
          paymentTokenId: item.paymentTokenId.toString(),
          buyerAmount: item.buyerAmount.toString(),
          tokenUri: item.tokenUri,
        })),
      });
      // buyNft's arity depends on the market generation: V2 splits placement
      // into PlacementInput{referrer, parent} where the referrer earns the
      // referral cut and the parent is the binary-tree position. Both are
      // resolved by usePlacementResolution and passed through as-typed --
      // never defaulted to one another here. See buyNftPlacementArgs.
      const buyNftTxHash = await writeContractAsync({
        address: ContractAddress.MARKET_NFT,
        abi: BUY_NFT_ABI,
        functionName: "buyNft",
        args: [
          ...buyNftPlacementArgs(
            parentAddress as `0x${string}`,
            referrerAddress as `0x${string}`,
          ),
          purchaseItems,
        ] as never,
      });

      const buyNftReceipt = await waitAndVerifyTransaction(
        buyNftTxHash,
        "buy_nft",
      );

      // Parse order IDs from receipt
      let orderIds: bigint[] = [];
      try {
        const gatewayLogs = buyNftReceipt.logs.filter(
          (log) =>
            log.address.toLowerCase() === ContractAddress.GATEWAY.toLowerCase(),
        );
        const orderEvents = parseEventLogs({
          abi: GATEWAY_ABI,
          logs: gatewayLogs,
          eventName: "NewOrderEvent",
        });
        orderIds = orderEvents.map((event) => event.args.id);
      } catch {
        // Non-fatal — order IDs are helpful but not required
      }

      // Step 6: Fetch user data
      logger.step("fetch_user");
      const user = await publicClient.readContract({
        address: ContractAddress.GATEWAY,
        abi: GATEWAY_ABI,
        functionName: "getUser",
        args: [address as `0x${string}`],
      });

      // The wallet's ACTUAL on-chain referrer, read fresh post-tx -- not
      // whatever was typed into the form. This is what gets mirrored to the
      // backend below. V1 has no `referrer` field on User at all.
      const onChainReferrer =
        "referrer" in user ? (user.referrer as `0x${string}`) : undefined;

      // Build payment data
      const { paidPrice, itemsPayment } = buildOrderPaymentData(
        cartItems,
        preparedItems,
        orderIds,
      );

      const result = {
        success: true,
        itemCount: preparedItems.length,
        txHash: buyNftTxHash,
        paidPrice,
        itemsPayment,
        userId: user.id,
        onChainReferrer,
      };

      // Log success (fire-and-forget)
      logger.complete({
        txHash: buyNftTxHash,
        orderIds: orderIds.map((id) => id.toString()),
        itemCount: preparedItems.length,
      });

      return result;
    } catch (error) {
      // Log failure with full step context (fire-and-forget)
      logger.fail(error, {
        orderType: params.orderType,
        cartItemCount: params.cartItems?.length,
        preparationId: params.preparationId,
      });
      throw error;
    }
  };

  return useMutation({
    mutationKey: ["checkout-purchase", address],
    mutationFn,
    onSuccess: (data, variables) => {
      // Best-effort DB mirror — fire-and-forget. Sends the wallet's REAL
      // on-chain referrer (read fresh post-tx, see onChainReferrer above),
      // never the typed form value: the backend sync is idempotent and
      // self-healing precisely because it only ever mirrors chain truth.
      const hasReferrer =
        !!data.onChainReferrer && data.onChainReferrer !== zeroAddress;
      updateUserRole.mutate({
        body: {
          usedRefererAddress: hasReferrer,
          referrerAddress: data.onChainReferrer ?? zeroAddress,
        },
      });

      // Persist the finalize payload for /checkout/success to replay POST /order.
      // We deliberately do NOT await the finalize here: an awaited network call
      // inside React Query's onSuccess is a wide window where a tab-close or a
      // thrown error strands the paid-for order (and a throw here is swallowed,
      // never routed to onError). The success route owns the finalize and makes
      // it retryable. user_id is stringified because data.userId is a bigint
      // (not JSON-serializable). See
      // docs/superpowers/specs/2026-06-02-checkout-success-redirect-page-design.md
      const order: components["schemas"]["CreateOrderDto"] = {
        shipping_address_id: variables.shippingAddressId,
        order_type: variables.orderType,
        tx_hash: data.txHash,
        referral_address: hasReferrer ? data.onChainReferrer : undefined,
        paid_price: data.paidPrice,
        items_payment: data.itemsPayment,
        user_id: data.userId.toString(),
        preparation_id: variables.preparationId,
      };
      persistPendingFinalize({ order, itemCount: data.itemCount });

      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      // Show Persian error message to user (single toast — not duplicated)
      handleContractError(error);

      if (options?.onError) {
        const errorObj =
          error instanceof Error ? error : new Error("Transaction failed");
        options.onError(errorObj);
      }
    },
  });
};
